import React, { useState } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useCollectionStore } from '../lib/collectionStore';
import { log } from '../components/DebugLogger';
import { useProfileStore } from '../lib/supabase';

export type CardFinish = 'normal' | 'holofoil' | 'reverse_holofoil' | 'poke_ball' | 'master_ball';

interface CollectionButtonProps {
  card: Card;
  compact?: boolean;
  defaultFinish?: CardFinish;
}

const finishOptions: { value: CardFinish; label: string; color: string }[] = [
  { value: 'normal', label: 'Normal', color: 'border-yellow-500/30' },
  { value: 'holofoil', label: 'Holofoil', color: 'border-purple-500/30' },
  { value: 'reverse_holofoil', label: 'Reverse Holofoil', color: 'border-blue-500/30' },
  { value: 'poke_ball', label: 'Poké Ball Pattern', color: 'border-red-500/30' },
  { value: 'master_ball', label: 'Master Ball Pattern', color: 'border-pink-500/30' }
];

export default function CollectionButton({ card, compact = false, defaultFinish = 'normal' }: CollectionButtonProps) {
  const { isAuthenticated } = useProfileStore();
  const { addCard, removeCard, getCardQuantity } = useCollectionStore();
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = async (finish: CardFinish, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      log('User not authenticated, redirecting to auth page');
      window.location.href = '/auth?register=true';
      return;
    }

    if (!card || !card.set?.id) {
      log('Invalid card data:', card);
      console.error('Invalid card data:', card);
      return;
    }

    setIsAdding(true);
    
    try {
      await addCard(card, 1, finish);
      log(`Added ${card.name} (${finish})`);
      
      const event = new CustomEvent('cardAdded', { 
        detail: { cardId: card.id }
      });
      document.dispatchEvent(event);
    } catch (error) {
      log(`Error adding card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (finish: CardFinish, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      return;
    }

    setIsAdding(true);
    
    try {
      await removeCard(card.id, finish, false); // Pass false to only remove one copy
      log(`Removed one copy of ${card.name} (${finish})`);
    } catch (error) {
      log(`Error removing card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-1">
      {finishOptions.map((option) => {
        const quantity = getCardQuantity(card.id, option.value);
        
        return (
          <button
            key={option.value}
            onClick={(e) => handleAdd(option.value, e)}
            onContextMenu={(e) => handleRemove(option.value, e)}
            disabled={isAdding}
            className={`
              relative flex items-center justify-center w-6 h-6 rounded border transition-all
              ${option.color}
              ${quantity > 0 
                ? 'bg-white/20 shadow-lg' 
                : 'bg-white/5 hover:bg-white/10'
              }
              ${isAdding ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={`${option.label} (Left click to add, right click to remove)`}
          >
            <span className="text-xs font-medium">
              {quantity > 0 ? quantity : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}